import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getApiBaseUrl } from './utils/api';
import { PublicClientApplication, BrowserCacheLocation } from '@azure/msal-browser';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [refreshToken, setRefreshToken] = useState(localStorage.getItem('refreshToken'));
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [onTenantChange, setOnTenantChange] = useState(null);
  const [availableTenants, setAvailableTenants] = useState([]);
  const [hasAllTenantAccess, setHasAllTenantAccess] = useState(false);

  // MSAL instance
  const msalClientRef = React.useRef(null);
  const ensureMsalClient = async () => {
    if (msalClientRef.current) return msalClientRef.current;
    // Fetch MSAL config from backend (template tenant)
    const resp = await fetch(`${getApiBaseUrl()}/api/auth/msal/config`);
    if (!resp.ok) {
      const errorText = await resp.text();
      console.error('MSAL config fetch failed:', resp.status, errorText);
      throw new Error(`Failed to load MSAL config: ${resp.status} ${errorText}`);
    }
    const cfg = await resp.json();
    msalClientRef.current = new PublicClientApplication({
      auth: {
        clientId: cfg.clientId,
        authority: `https://login.microsoftonline.com/${cfg.tenant || 'common'}`,
        redirectUri: 'https://portal.solidit.dk/'
      },
      cache: {
        cacheLocation: BrowserCacheLocation.LocalStorage,
        storeAuthStateInCookie: false
      }
    });
    return msalClientRef.current;
  };

  // Initialize MSAL (required in some versions/environments) and handle any redirect responses
  useEffect(() => {
    const initMsal = async () => {
      try {
        const client = await ensureMsalClient();
        await client.initialize?.();
        // Handle redirect response if any (even though we're using popup, this is safe)
        await client.handleRedirectPromise?.();
        const accounts = client.getAllAccounts();
        if (accounts && accounts.length > 0) {
          client.setActiveAccount(accounts[0]);
        }
      } catch (e) {
        // Non-fatal for popup flow
        // eslint-disable-next-line no-console
        console.error('MSAL init error:', e);
      }
    };
    initMsal();
  }, []);

  // Logout function - defined early so it can be used by refreshAccessToken
  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    setRefreshToken(null);
    setSelectedTenant(null);
    setAvailableTenants([]);
    setHasAllTenantAccess(false);
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
  }, []);

  // Function to refresh access token
  const refreshAccessToken = useCallback(async () => {
    if (!refreshToken) {
      return false;
    }

    try {
      const response = await fetch(`${getApiBaseUrl()}/api/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken }),
      });

      if (response.ok) {
        const data = await response.json();
        setToken(data.token);
        setRefreshToken(data.refreshToken);
        localStorage.setItem('token', data.token);
        localStorage.setItem('refreshToken', data.refreshToken);
        return true;
      } else {
        // Refresh token is invalid, logout user
        logout();
        return false;
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      logout();
      return false;
    }
  }, [refreshToken, logout]);

  // Verify token on app load
  useEffect(() => {
    const verifyToken = async () => {
      if (token) {
        try {
          const response = await fetch(`${getApiBaseUrl()}/api/auth/verify`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            setUser(data.user);
          } else if (response.status === 401) {
            // Token expired, try to refresh
            const refreshed = await refreshAccessToken();
            if (refreshed) {
              // Retry verification with new token
              const newResponse = await fetch(`${getApiBaseUrl()}/api/auth/verify`, {
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
              });
              if (newResponse.ok) {
                const data = await newResponse.json();
                setUser(data.user);
              }
            }
          } else {
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
            setToken(null);
            setRefreshToken(null);
          }
        } catch (error) {
          console.error('Token verification failed:', error);
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          setToken(null);
          setRefreshToken(null);
        }
      }
      setLoading(false);
    };

    verifyToken();
  }, [token, refreshToken, refreshAccessToken]);

  const login = async (username, password) => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();

      if (response.ok) {
        setUser(data.user);
        setToken(data.token);
        setRefreshToken(data.refreshToken);
        localStorage.setItem('token', data.token);
        localStorage.setItem('refreshToken', data.refreshToken);
        return { success: true };
      } else {
        return { success: false, error: data.error };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  };

  // Login with Microsoft: acquire an ID token, send to backend /api/auth/msal to exchange for portal JWT
  const loginWithMsal = async () => {
    try {
      const client = await ensureMsalClient();
      // Prefer popup to avoid full-page redirects
      const loginResponse = await client.loginPopup({
        scopes: ['openid', 'profile', 'email']
      });
      const account = loginResponse.account || client.getActiveAccount() || (client.getAllAccounts()[0] || null);
      if (account) {
        client.setActiveAccount(account);
      }

      // Get ID token silently when possible
      const tokenResp = await client.acquireTokenSilent({
        scopes: ['openid', 'profile', 'email'],
        account: account || undefined
      }).catch(async () => {
        return client.acquireTokenPopup({ scopes: ['openid', 'profile', 'email'] });
      });

      const idToken = tokenResp.idToken;
      if (!idToken) {
        return { success: false, error: 'Failed to acquire ID token' };
      }

      const response = await fetch(`${getApiBaseUrl()}/api/auth/msal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ idToken })
      });
      const data = await response.json();
      if (!response.ok) {
        return { success: false, error: data.error || 'MSAL login failed' };
      }

      setUser(data.user);
      setToken(data.token);
      setRefreshToken(data.refreshToken);
      localStorage.setItem('token', data.token);
      localStorage.setItem('refreshToken', data.refreshToken);
      return { success: true };
      } catch (err) {
        console.error('MSAL login error:', err);
        if (err.errorCode === 'AADSTS500113') {
          return { success: false, error: 'Azure AD app needs redirect URI configured. Please add this URL to your Azure AD app registration: https://portal.solidit.dk/' };
        }
        return { success: false, error: 'Microsoft sign-in failed: ' + (err.errorMessage || err.message || 'Unknown error') };
      }
  };


  const updateSelectedTenant = (tenant) => {
    setSelectedTenant(tenant);
    if (onTenantChange) {
      onTenantChange(tenant);
    }
  };

  // Utility function for making authenticated API calls with automatic token refresh
  const authenticatedFetch = useCallback(async (url, options = {}) => {
    let response = await fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': `Bearer ${token}`
      }
    });

    // If token expired, try to refresh and retry
    if (response.status === 401) {
      const refreshed = await refreshAccessToken();
      if (refreshed) {
        // Retry with new token
        response = await fetch(url, {
          ...options,
          headers: {
            ...options.headers,
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
      }
    }

    return response;
  }, [token, refreshAccessToken]);

  // Populate tenants globally and auto-select when needed
  useEffect(() => {
    const loadTenants = async () => {
      if (!user || !token) return;
      try {
        const resp = await authenticatedFetch(`${getApiBaseUrl()}/api/tenants`);
        if (!resp.ok) {
          setHasAllTenantAccess(false);
          return;
        }
        const data = await resp.json();
        if (Array.isArray(data.tenants)) {
          setAvailableTenants(data.tenants);
          setHasAllTenantAccess(Boolean(data.hasAllAccess));
          if (!selectedTenant && data.tenants.length > 0) {
            // Auto-select the only tenant or the first in list
            setSelectedTenant(data.tenants[0]);
            if (onTenantChange) onTenantChange(data.tenants[0]);
          }
        } else {
          setHasAllTenantAccess(false);
        }
      } catch (e) {
        // ignore for now
        setHasAllTenantAccess(false);
      }
    };
    loadTenants();
  }, [user, token, authenticatedFetch, onTenantChange, selectedTenant]);

  const value = {
    user,
    token,
    login,
    loginWithMsal,
    logout,
    loading,
    isAuthenticated: !!user,
    selectedTenant,
    setSelectedTenant: updateSelectedTenant,
    setOnTenantChange,
    availableTenants,
    setAvailableTenants,
    hasAllTenantAccess,
    authenticatedFetch
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
